import type { IncentiveStore, IncentiveStoreDAO } from "@/domains/incentive"
import type { OrchestratedQuery } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenStore } from "@/domains/tokens"
import { LcAddress } from "@/domains/core"

import type { GaugeApiData } from "./types"

/**
 * Minimal interface for emissions calculation data
 * Extracted from ChainConfig to minimize dependencies deep in the stack
 */
export interface IEmissionsConfig {
  emitted: number
  decayFactor: number
  universalEpoch: number
}

/**
 * Interface for any entity that can generate revenue for a gauge
 * This allows gauges to connect to different revenue sources:
 * - Uniswap V2/V3 pools
 * - DLMM pools
 * - Lending protocols
 * - Vaults
 * - Whole protocols (for partnerships)
 */
export interface IRevenueSource {
  address: LcAddress
  displayName: string
  displayType: string
  tvlUsd: number
  tokens: Array<TokenStore>
  gaugeValueUSD: number // Total Value Locked in the gauge (staked value) in USD
  /** LP APR from the liquidity pool (e.g. MinimalPoolStore.apr). Omit for non-pool revenue sources. */
  apr?: number
}

/**
 * Top incentive data for display
 */
export interface ITopIncentive {
  address: LcAddress
  displayType: string
  displayName: string
  tokens: Array<TokenStore>
  /**
   * Total incentive value in USD (sum of all incentive rewardAmountUsd values)
   * This represents the total value of bribes/incentives, not the staked TVL
   */
  gaugeValueUSD: number
  /** LP APR from the liquidity pool (same as classic liquidity page APR column). */
  apr: number
  /** Voting (gauge) APR for veSCALE voters. */
  gaugeApr: number
}

/**
 * Gauge Store DAO - processed data for initializing GaugeStore
 */
export interface GaugeStoreDAO {
  address: LcAddress
  revenueSourceAddress: LcAddress
  bribeAddress: LcAddress
  apr: string
  aprLastEpoch: string
  rewardRate: string
  totalBribeValueUsd: string
  totalSupply: string
  votes: string
  votesPercent: string
  incentives: Array<IncentiveStoreDAO>
}

/**
 * Vote allocation for a gauge
 * Uses LcAddress for consistent lowercase address handling
 */
export interface Vote {
  address: LcAddress
  value: number
}

/**
 * Gauge stats calculated based on vote allocations
 */
export interface GaugeStats {
  totalVotes: number
  bribesPlusFees: number
  incomePerVote: number
  myVoteValue: number
  myAvailableVotes: number
  myVoteCount: number
  newInclusiveVoteCount: number
  newInclusiveIncomePerVote: number
  newExpectedIncome: number
  incomeEfficiency: number
  nextEmissions: number
}

/**
 * Last epoch vote data for a gauge
 */
export interface LastEpochVotes {
  count: number
  votePercent: number
}

/**
 * Interface for gauge API transport operations
 */
export interface IGaugeApiTransport {
  loadGaugeBribes(): Promise<Record<string, GaugeApiData>>
}

/**
 * Interface for gauge on-chain transport operations
 */
export interface IGaugeOnChainTransport {
  fetchGaugeVoteData(gaugeAddress: LcAddress): Promise<{ votes: string; votesPercent: string } | undefined>
}

/**
 * Interface for gauge repository operations
 */
export interface IGaugeRepository {
  add(gauge: IGauge): void
  find(gaugeAddress: LcAddress): IGauge | undefined
  findByRevenueSource(revenueSource: LcAddress): IGauge | undefined
  getAll(): IGauge[]
  clearAll(): void
  /**
   * Get top incentives sorted by total incentive value (sum of all incentive rewardAmountUsd) in descending order
   * Only includes gauges with connected revenue sources, valid tokens, and total incentive value > 0
   * @param count Maximum number of top incentives to return
   */
  getTopIncentivesByGaugeValue(count: number): Array<ITopIncentive>
}

/**
 * Constants for revenue source types
 */
export const REVENUE_SOURCE_TYPE = {
  MINIMAL_POOL: "minimal-pool",
  CL_FARM: "cl-farm",
} as const

/**
 * Type of revenue source that a connector strategy can find
 */
export type RevenueSourceType = (typeof REVENUE_SOURCE_TYPE)[keyof typeof REVENUE_SOURCE_TYPE]

/**
 * Interface for gauge connector strategy
 * Used to find revenue sources that match a gauge address
 */
export interface IGaugeConnectorStrategy {
  readonly revenueSourceType: RevenueSourceType
  findRevenueSourceByGauge(gauge: LcAddress): IRevenueSource | undefined
}

/**
 * Interface for gauge orchestrator
 */
export interface IGaugeOrchestrator {
  query: OrchestratedQuery<Record<string, GaugeApiData>>
  onGaugeBribesQuerySuccess(
    sender: IOrchestratedQuerySender<Record<string, GaugeApiData>>,
    data: Record<string, GaugeApiData>,
  ): void
  onGaugeBribesQueryFailed(sender: IOrchestratedQuerySender<Record<string, GaugeApiData>>, error: Error): void
  onApiLoadGaugeBribesTrigger(
    query: IOrchestratedQuerySender<Record<string, GaugeApiData>>,
  ): Promise<Record<string, GaugeApiData>>
}

/**
 * Interface for gauge entity
 */
export interface IGauge {
  address: LcAddress
  bribeAddress: LcAddress
  revenueSource: IRevenueSource | undefined
  revenueSourceAddress: LcAddress
  apr: string
  aprLastEpoch: string
  rewardRate: string
  totalBribeValueUsd: string
  totalSupply: string
  votes: number
  votesPercent: number
  incentives: Array<IncentiveStore>
  connectToRevenueSource(source: IRevenueSource): void
  updateVoteData(votes: string, votesPercent: string): void
  updateStaticData(data: Partial<GaugeStoreDAO>): void
  updateIncentives(daoIncentives: Array<IncentiveStoreDAO>): void
  /**
   * Get tokens from the connected revenue source, or empty array if not connected
   */
  get tokens(): Array<TokenStore>
  /**
   * Computed total incentive value in USD (sum of all incentive rewardAmountUsd values)
   * Updates reactively when incentives change or token prices change
   */
  get totalIncentiveValueUsd(): number
}

/**
 * Gauge namespace structure
 */
export type GaugeNamespace = {
  Repository: IGaugeRepository
  Orchestrator: IGaugeOrchestrator
}
